/* Buying more of something already held.
 *
 * The only way to do this was the add-a-position screen, which writes a new
 * lot — and for shares bought into an account that already holds them that is
 * wrong twice over. `Holding` is documented as one ticker inside one account,
 * so a second lot for the same pair is a shape nothing else expects; and the
 * average cost, which is the whole point of keeping a cost at all, ends up
 * split across two rows that each know half of it.
 *
 * So this averages instead, the way `addToPerp` does for contracts: the shares
 * add up, and the cost is what was paid for all of them divided by how many
 * there now are. Commission goes in, because it is money spent to acquire them
 * (see `state/commission.ts`).
 *
 * ── the case worth being careful about ────────────────────────────────────
 *
 * `avgCost` of zero is this book's word for "not known", not for "free". Adding
 * a lot with a known price to one with an unknown cost cannot produce a known
 * average — the answer would be the new purchase's price wearing the weight of
 * shares nobody has a cost for, which reads as fact and is a guess. So the
 * result stays unknown, and the caller is told why rather than handed a number.
 */

import type { Holding } from '../types';

export interface Purchase {
  shares: number;
  /** Per share, in the listing's currency. */
  price: number;
  /** What the broker took, in the same currency. */
  fee?: number;
}

export interface LotAddition {
  next: Holding;
  /** What the average was before, so a screen can show the move. */
  previousAvg: number;
  /** True when the lot had no cost on record, so the average is still unknown. */
  costStillUnknown: boolean;
  /** Total paid for this purchase, commission included. */
  paid: number;
}

/** Null when the addition does not describe a purchase: nothing bought, or a
    price at or below zero. Better to refuse than to average in a bad number. */
export function addToLot(lot: Holding, buy: Purchase): LotAddition | null {
  const bought = Math.abs(buy.shares);
  if (!(bought > 0) || !(buy.price > 0)) return null;

  const fee = Number.isFinite(buy.fee ?? 0) ? Math.abs(buy.fee ?? 0) : 0;
  const paid = bought * buy.price + fee;
  const totalShares = lot.shares + bought;
  if (!(totalShares > 0)) return null;

  const costStillUnknown = !(lot.avgCost > 0);
  return {
    next: {
      ...lot,
      shares: totalShares,
      avgCost: costStillUnknown ? 0 : (lot.shares * lot.avgCost + paid) / totalShares,
    },
    previousAvg: lot.avgCost,
    costStillUnknown,
    paid,
  };
}

/** The lot a purchase into this account would land on, if there is one. Ties
    are not possible: one ticker in one account is one lot, which is exactly the
    rule this exists to keep. */
export function lotFor(holdings: Holding[], ticker: string, account: string): Holding | undefined {
  return holdings.find((h) => h.ticker === ticker && h.account === account);
}

/* Shares joining a lot that already exists, from wherever they came.
 *
 * `addToLot` above answers "I bought more of this". This answers the narrower
 * and stricter question "these shares are now in this lot" — a transfer between
 * accounts, or two rows for one ticker+account being joined back into the one
 * row the model allows. It is deliberately not `addToLot` with a flag:
 *
 *   - `addToLot` refuses a price at or below zero, because a purchase at no
 *     price is a bad input. Here it is the ordinary case for shares whose cost
 *     was never recorded, and refusing would leave the duplicate row standing.
 *   - There is no fee in the numerator. A commission is money spent to acquire
 *     shares (`state/commission.ts`); these were acquired already, and the cost
 *     of moving them is not part of what they cost.
 *
 * The rule it keeps is **conservation of recorded cost**: what the two rows
 * carried between them before equals what the one row carries after. That is
 * why an unknown cost on *either* side poisons the result, where `addToLot`
 * only checks the lot it is adding to — blending a known cost with an unknown
 * one produces a number that reads as a fact and is a guess about shares
 * nobody has a cost for.
 *
 * One implementation, because there are three doors to this: a transfer, a
 * rename onto a name already held in the same account, and joining duplicates
 * the old Account dropdown left behind. Three copies of this arithmetic would
 * be three chances to get it subtly different. */

export interface ArrivingShares {
  shares: number;
  /** Per share, in the lot's listing currency. Undefined means not known. */
  costPerShare?: number | undefined;
}

/** Null when nothing arrives. The caller checks the currencies match: this
    cannot, because it is handed a quantity rather than a lot. */
export function mergeLots(dest: Holding, arriving: ArrivingShares): Holding | null {
  const m = Math.abs(arriving.shares);
  if (!(m > 0)) return null;

  const total = dest.shares + m;
  if (!(total > 0)) return null;

  const arrivingKnown = (arriving.costPerShare ?? 0) > 0;
  const destKnown = dest.avgCost > 0;
  /* Either side unknown leaves it unknown. A blended average is only a fact
     when both halves of it were. */
  const unknown = !arrivingKnown || !destKnown;

  return {
    ...dest,
    shares: total,
    avgCost: unknown
      ? 0
      : (dest.shares * dest.avgCost + m * (arriving.costPerShare ?? 0)) / total,
  };
}
